<template>
  <b-modal
    lazy
    size="lg"
    hide-header-close
    header-class="v2_bg_primary_teal"
    modal-class="font_Montserrat"
    :visible="visible"
  >
    <template slot="modal-header">
      <span
        class="align-self-center my-0 v2_color_white v2_font_weight_semibold v2_font_size_24"
      >
        Payment
      </span>
    </template>
    <b-row>
      <b-col>
        Thank you for your company information. Complete this payment page to purchase "Connect".
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col>
        What's Included:
        <p class="mb-0"><i class="mdi mdi-18px mdi-check"></i>Access Phairify's exclusive pool of candidates in the specialty and subspecialty of interest.</p>
        <p class="mb-0"><i class="mdi mdi-18px mdi-check"></i>Access Inform - specialty-specific market intelligence to build a market-relevant job offer.</p>
        <p class="mb-0"><i class="mdi mdi-18px mdi-check"></i>We connect your opportunity to physicians with and expressed interest precisely in your job.</p>
        <p class="mb-0"><i class="mdi mdi-18px mdi-check"></i>Our matching algorithm searches for any new matching candidates for 90-days.</p>
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col>
      By clicking "Complete Order", I agree that I have read and accepted the
      <a
          href="https://phairify-docs.s3.us-east-2.amazonaws.com/Phairify_Terms_Conditions_Use.pdf"
          target="_blank"
      >
        Terms of Use</a>, See our <a
        href="https://phairify-docs.s3.us-east-2.amazonaws.com/Phairify_Privacy_Policy_2020_09_01.pdf"
        target="_blank"
    > Privacy Policy </a> for details or to opt-out at any time.
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <div
          v-if="stripeErrors"
          class="invalid-feedback"
          :class="{ 'd-block': !!stripeErrors }"
        >
          {{ stripeErrors }}
        </div>
        <b-row class="mt-2">
          <b-col cols="8">
            <b-form-group label="Card Number *">
              <div class="form-control d-flex align-items-center">
                <div ref="cardNumber" class="flex-grow-1"></div>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group label="CVV *">
              <div class="form-control d-flex align-items-center">
                <div ref="cardCvc" class="flex-grow-1"></div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="8">
            <b-form-group
              label="Card Holder Name *"
              :invalid-feedback="cardHolderNameInvalidFeedback"
              :state="validateState('cardHolderName')"
            >
              <b-form-input
                v-model="$v.cardHolderName.$model"
                type="text"
                required
                :state="validateState('cardHolderName')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="8">
            <b-form-group label="Expiration Date *">
              <div class="form-control d-flex align-items-center">
                <div ref="cardExpiry" class="flex-grow-1"></div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="8">
            <b-form-group label="Duration" description="Months">
              <b-form-input
                v-model="$v.duration.$model"
                type="number"
                min="1"
                max="100"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <template slot="modal-footer">
      <div class="flex-grow-1"></div>
      <div class="flex-grow-1 d-flex align-items-center justify-content-around">
        <b-button variant="v3_teal_outline" @click="completeOrder">
          <template v-if="!completeLoading">Complete order</template>
          <template v-else>
            <b-spinner small />
          </template>
        </b-button>
        <b-button variant="v3_teal_outline" @click="$emit('cancel')">
          Cancel
        </b-button>
      </div>
      <div
        class="flex-grow-1 d-flex align-items-center justify-content-center v2_font_size_28"
      >
        <span class="v2_color_med_gray">Total:</span>&nbsp;
        <span class="v2_color_primary_teal">${{ total }}</span>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { alphaSpaceValidator } from '@/helpers/customValidators';
import Api from '@/providers/Api';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import { loadStripe } from '@stripe/stripe-js';

export default {
  name: 'PurchaseModal',
  mixins: [validationMixin],
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  stripe: null,
  elements: null,
  cardNumberElement: null,
  cardCvvElement: null,
  cardExpiry: null,
  data: function () {
    return {
      total: 1500,
      cardHolderName: null,
      stripeErrors: null,
      cs: null,
      completeLoading: false,
      duration: 3,
    };
  },
  validations: {
    cardHolderName: {
      required,
      alphaSpaceValidator,
    },
    duration: {},
  },
  mounted: async function () {
    this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
    this.elements = this.stripe.elements();

    this.cardNumberElement = this.elements.create('cardNumber');
    this.cardNumberElement.mount(this.$refs.cardNumber, {});
    this.cardNumberElement.on('change', (event) => this.setOutcome(event));

    this.cardCvvElement = this.elements.create('cardCvc');
    this.cardCvvElement.mount(this.$refs.cardCvc, {});

    this.cardExpiry = this.elements.create('cardExpiry');
    this.cardExpiry.mount(this.$refs.cardExpiry, {});
  },
  computed: {
    cardHolderNameInvalidFeedback: function () {
      if (!this.$v.cardHolderName.alphaSpaceValidator) {
        return 'Only alpha characters are allowed';
      }
      return 'Card Holder Name is required';
    },
  },
  methods: {
    validateState: function (name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
    setOutcome: function (result) {
      if (result.error) {
        this.stripeErrors = result.error.message;
      } else {
        this.stripeErrors = null;
      }
    },
    completeOrder: async function () {
      this.stripeErrors = null;
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      this.completeLoading = true;

      const options = {
        name: this.cardHolderName,
      };

      try {
        const result = await this.stripe.createToken(
          this.cardNumberElement,
          options
        );
        if (result.error) {
          this.setOutcome(result);
          return;
        }
        const jobOffer = await Api.purchaseJobOffer(result.token.id, {
          amount: this.total,
          duration: this.duration,
        });

        this.$toasted.success('Payment was successful', { duration: 3000 });

        // if (this.$cookies.isKey(COOKIES.GUEST_ICO_BROWSER_CRITERIA_ITEM_VALUES)) {
        //   this.$cookies.remove(COOKIES.GUEST_ICO_BROWSER_CRITERIA_ITEM_VALUES);
        // }

        this.$emit('job-offer-paid', jobOffer);
        this.$router.push(`/recruiter/edit-job-offer/${jobOffer.id}`);
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.completeLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../sass/variables_mixins.scss';
.v2_bg_primary_teal {
  background-color: $phairify_v2_color_primary_teal;
}
</style>
